"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  payWithCard,
  payWithPix,
  confirmPixPayment,
  CardDataForHash,
  getPriceQuote,
} from "../../../services/bookingPaymentClient";
import { createPaymentCardToken } from "../../../services/paymentTokenization";

import { useBookingPayment } from "../hooks/useBookingPayment";
import { useFeedbackState } from "../../../components/ui/ToastProvider";

type Props = {
  show: boolean;
  onClose: () => void;
  establishmentId: string | null;
  bookingPayload?: any | null;
  amountCents?: number | null;
  payerEmail?: string | null;
  onPaymentApproved?: (confirmation: any) => void;
  passCardFeesToClient?: "SIM" | "NÃO" | null;
  ensureHoldForPayment?: () => Promise<{
    appointmentId: string;
    expiresAt?: string | null;
  }>;
};

export function BookingPaymentModal({
  show,
  onClose,
  establishmentId,
  bookingPayload,
  amountCents,
  payerEmail,
  onPaymentApproved,
  passCardFeesToClient,
  ensureHoldForPayment,
}: Props) {
  const [paymentMethod, setPaymentMethod] = useState<"credit" | "debit" | "pix" | null>(
    "credit"
  );
  const [showPixQrCode, setShowPixQrCode] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardMonth, setCardMonth] = useState("");
  const [cardYear, setCardYear] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [cardCpf, setCardCpf] = useState("");
  const [pixPhone, setPixPhone] = useState(
    bookingPayload?.phone || bookingPayload?.whatsapp || ""
  );
  const [billingZipCode, setBillingZipCode] = useState("");
  const [billingStreet, setBillingStreet] = useState("");
  const [billingNumber, setBillingNumber] = useState("");
  const [billingComplement, setBillingComplement] = useState("");
  const [billingNeighborhood, setBillingNeighborhood] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [paymentError, setPaymentError] = useFeedbackState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const paymentSdkReady = true;
  const [pixResponse, setPixResponse] = useState<any | null>(null);
  const [quotedAmountCents, setQuotedAmountCents] = useState<number | null>(null);
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [pixConfirming, setPixConfirming] = useState(false);
  const autoPixRequestedRef = useRef(false);
  const pixAutoCheckStartedRef = useRef(false);
  const mountedRef = useRef<{ sdkReady?: boolean } | null>(null);
  const mountAttemptedRef = useRef(false);
  const { refundFeePercentage, loadRefundFeePercentage } = useBookingPayment();

  function formatBRL(value: number) {
    try {
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    } catch {
      return `R$ ${(value / 100).toFixed(2)}`;
    }
  }

  const getErrorMessage = (error: string): string => {
    if (error.includes("Configuração de pagamento")) return error;

    const errorMap: Record<string, string> = {
      "Pagamento recusado pelo emissor":
        "O pagamento foi recusado pelo banco emissor. Tente outro método ou cartão.",
      "Saldo insuficiente":
        "Saldo insuficiente. Tente outro cartão ou método de pagamento.",
      "Número do cartão inválido":
        "Número do cartão inválido. Verifique os dados e tente novamente.",
      "Data de validade inválida":
        "Data de validade inválida. Verifique mês/ano e tente novamente.",
      "Código de segurança inválido":
        "Código de segurança inválido. Verifique o CVV e tente novamente.",
      "Pagamento recusado: autorização necessária":
        "O banco exigiu autorização. Entre em contato com seu banco ou tente outro cartão.",
      "Cartão bloqueado ou inválido": "Cartão bloqueado/inválido. Tente outro cartão.",
      "Muitas tentativas incorretas":
        "Muitas tentativas incorretas. Aguarde um pouco ou tente outro cartão.",
      invalid_payment_method:
        "Método de pagamento inválido. Por favor, verifique os dados do cartão.",
      card_declined: "Cartão recusado. Tente outro cartão ou método de pagamento.",
      insufficient_funds:
        "Saldo insuficiente. Tente outro cartão ou método de pagamento.",
      expired_card: "Cartão expirado. Por favor, utilize um cartão válido.",
    };
    return errorMap[error] || "Não foi possível concluir o pagamento. Tente novamente.";
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    const formatted = cleaned.match(/.{1,4}/g)?.join(" ") || cleaned;
    return formatted.slice(0, 19);
  };

  const formatCpf = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 11) {
      return cleaned
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return value;
  };

  const formatCep = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    return cleaned.replace(/(\d{5})(\d)/, "$1-$2").slice(0, 9);
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    const ddd = digits.slice(0, 2);
    const part1 = digits.length > 10 ? digits.slice(2, 7) : digits.slice(2, 6);
    const part2 = digits.length > 10 ? digits.slice(7, 11) : digits.slice(6, 10);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${ddd}) ${part1}`;
    return `(${ddd}) ${part1}-${part2}`;
  };

  const normalizePhoneDigits = (value: string) => {
    let digits = value.replace(/\D/g, "");
    if (digits.startsWith("55") && digits.length > 11) {
      digits = digits.slice(2);
    }
    return digits.slice(0, 11);
  };

  const handlePaymentMethodChange = (method: "credit" | "debit" | "pix" | null) => {
    setPaymentMethod(paymentMethod === method ? null : method);
    setPaymentError(null);
  };

  const effectiveCents = (() => {
    // Crédito ou Débito → usa valor com taxa
    if (
      (paymentMethod === "credit" || paymentMethod === "debit") &&
      quotedAmountCents != null
    ) {
      return quotedAmountCents;
    }

    // PIX ou nada selecionado → valor base, sem taxa
    return amountCents ?? null;
  })();

  const displayAmount =
    effectiveCents != null ? formatBRL(effectiveCents / 100) : "Valor não informado";

  useEffect(() => {
    if (!show) return;
    if (mountAttemptedRef.current) return;
    mountAttemptedRef.current = true;
  }, [show]);

  useEffect(() => {
    if (!show) return;
    if (pixPhone) return;
    const fromPayload = bookingPayload?.phone || bookingPayload?.whatsapp || "";
    if (fromPayload) setPixPhone(fromPayload);
  }, [show, bookingPayload?.phone, bookingPayload?.whatsapp, pixPhone]);

  useEffect(() => {
    if (!show) return;
    if (paymentMethod !== "pix") {
      autoPixRequestedRef.current = false;
      return;
    }
    if (pixResponse || loading) return;

    const autoKey =
      bookingPayload?.whatsapp ||
      bookingPayload?.phone ||
      bookingPayload?.email ||
      payerEmail ||
      null;

    if (autoKey) {
      autoPixRequestedRef.current = true;
    }
  }, [show, paymentMethod, pixResponse, loading, bookingPayload, payerEmail]);

  useEffect(() => {
    if (!show) return;
    (async () => {
      try {
        if (!establishmentId) return;
        const svcIds: string[] | undefined =
          bookingPayload?.serviceIds ||
          (bookingPayload?.serviceId ? [bookingPayload.serviceId] : undefined);
        if (!svcIds || svcIds.length === 0) return;

        const paymentType =
          paymentMethod === "pix"
            ? "pix"
            : paymentMethod === "debit"
              ? "debit"
              : "credit";
        const quote = await getPriceQuote({
          establishmentId,
          paymentType,
          creditPayoutTerm: "D14",
          serviceIds: svcIds,
        });
        const cents = Math.round((quote as any).finalAmount * 100);
        setQuotedAmountCents(cents);
        setFeeAmount((quote as any).feeAmount ?? 0);
      } catch (e) {
        setQuotedAmountCents(amountCents ?? null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    paymentMethod,
    establishmentId,
    bookingPayload?.serviceId,
    bookingPayload?.serviceIds,
  ]);

  function validateCardNative() {
    if (!cardNumber || !cardMonth || !cardYear || !cardCvv || !cardName)
      return "Todos os campos do cartão são obrigatórios.";
    if (!cardCpf) return "Informe o CPF do titular.";

    const mm = Number(cardMonth);
    const yyyy = Number(cardYear);

    if (!(mm >= 1 && mm <= 12)) return "Mês de expiração inválido.";
    if (String(yyyy).length !== 4) return "Ano de expiração inválido (AAAA).";

    return null;
  }

  function clearSensitiveFields() {
    setCardNumber("");
    setCardMonth("");
    setCardYear("");
    setCardCvv("");
    setCardName("");
  }

  const handleCardPayment = async (isDebit = false) => {
    setPaymentError(null);

    const nativeErr = validateCardNative();
    if (nativeErr) {
      setPaymentError(nativeErr);
      return;
    }
    if (!establishmentId) {
      setPaymentError("Estabelecimento inválido.");
      return;
    }
    if (!amountCents && effectiveCents == null) {
      setPaymentError("Valor do serviço não informado.");
      return;
    }
    const payerEmailAddress = payerEmail || bookingPayload?.email;
    if (!payerEmailAddress) {
      setPaymentError("Informe o e-mail do pagador.");
      return;
    }
    if (!paymentSdkReady) {
      setPaymentError(
        "Sistema de pagamentos ainda não carregado. Aguarde e tente novamente."
      );
      return;
    }
    if (
      !billingZipCode ||
      !billingStreet ||
      !billingNumber ||
      !billingCity ||
      !billingState
    ) {
      setPaymentError("Preencha todos os campos de endereço de cobrança.");
      return;
    }

    setLoading(true);
    try {
      let appointmentId: string | null =
        bookingPayload?.id || bookingPayload?.appointmentId || null;

      if (!appointmentId) {
        if (!ensureHoldForPayment) {
          throw new Error("Não foi possível reservar o horário para pagamento.");
        }
        const hold = await ensureHoldForPayment();
        appointmentId = hold?.appointmentId || null;
      }

      if (!appointmentId) {
        throw new Error("Não foi possível reservar o horário para pagamento.");
      }

      const cardPayload: CardDataForHash = {
        card_number: cardNumber.replace(/\s+/g, ""),
        card_holder_name: cardName,
        card_expiration_date: `${cardMonth.padStart(2, "0")}${String(cardYear).slice(
          -2
        )}`,
        card_cvv: cardCvv,
      };

      const cardToken = await createPaymentCardToken(cardPayload, {
        kind: "appointment",
        establishmentId,
      });

      clearSensitiveFields();

      const finalAmountCents = quotedAmountCents ?? amountCents;

      if (finalAmountCents == null) {
        throw new Error("Valor do pagamento não definido");
      }

      const payerPhoneDigits = normalizePhoneDigits(
        bookingPayload?.phone || bookingPayload?.whatsapp || ""
      );
      const resp = await payWithCard({
        appointmentId,
        establishmentId,
        transactionAmount: finalAmountCents / 100,
        payerEmail: payerEmailAddress,
        payerPhone: payerPhoneDigits || undefined,
        payerCpf: cardCpf.replace(/\D/g, ""),
        payerName: cardName,
        description: bookingPayload?.serviceName || "Pagamento online",
        installments: isDebit ? 1 : 1,
        billingAddress: {
          zipCode: billingZipCode.replace(/\D/g, ""),
          street: billingStreet.trim(),
          number: billingNumber.trim(),
          complement: billingComplement || undefined,
          neighborhood: billingNeighborhood?.trim() || undefined,
          city: billingCity.trim(),
          state: billingState.trim(),
          country: "BR",
        },
        cardHash: cardToken.type === "card_hash" ? cardToken.value : undefined,
        cardToken: cardToken.type === "card_token" ? cardToken.value : undefined,
        cardCvvStartsWithSix: cardCvv.replace(/\D/g, "").startsWith("6"),
      });

      const status = String(resp?.status || "").toLowerCase();
      if (resp?.success === true || status === "approved" || status === "paid") {
        onPaymentApproved?.(resp);
        onClose();
      } else {
        setPaymentError(
          resp?.message || "Pagamento em análise. Aguarde e tente novamente."
        );
      }
    } catch (err: any) {
      console.error("BookingPaymentModal.handleCardPayment error:", err);
      const msg =
        String(err?.message || err) || "Erro ao processar pagamento com cartão.";
      setPaymentError(getErrorMessage(msg));
    } finally {
      setLoading(false);
    }
  };

  const handlePixConfirm = async (forcedPixKey?: string | null | any) => {
    setPaymentError(null);
    setPixResponse(null);
    setPixConfirming(false);
    pixAutoCheckStartedRef.current = false;

    const safeForcedKey = typeof forcedPixKey === "string" ? forcedPixKey : null;

    if (!establishmentId) {
      setPaymentError("Estabelecimento inválido.");
      return;
    }
    if (!amountCents && effectiveCents == null) {
      setPaymentError("Valor do serviço não informado.");
      return;
    }

    const emailForPix = payerEmail || bookingPayload?.email || null;
    if (!emailForPix) {
      setPaymentError("E-mail do pagador é obrigatório para gerar o PIX.");
      return;
    }

    const payerCpfForPix = bookingPayload?.cpf || cardCpf;
    const payerNameForPix =
      bookingPayload?.clientName || bookingPayload?.name || cardName;

    const payerCpfDigits = payerCpfForPix
      ? String(payerCpfForPix).replace(/\D/g, "")
      : "";
    const isValidCpf = (value: string) => {
      if (!value || value.length !== 11) return false;
      if (/^(\d)\1{10}$/.test(value)) return false;
      const calc = (base: string, factor: number) => {
        let total = 0;
        for (let i = 0; i < base.length; i += 1) {
          total += Number(base[i]) * (factor - i);
        }
        const mod = (total * 10) % 11;
        return mod === 10 ? 0 : mod;
      };
      const d1 = calc(value.slice(0, 9), 10);
      const d2 = calc(value.slice(0, 9) + d1, 11);
      return value.endsWith(`${d1}${d2}`);
    };
    if (!payerCpfDigits || !isValidCpf(payerCpfDigits)) {
      setPaymentError("CPF do pagador é inválido.");
      return;
    }
    if (!payerNameForPix || String(payerNameForPix).trim().length < 2) {
      setPaymentError("Nome do pagador é obrigatório para gerar o PIX.");
      return;
    }

    const payerPhoneForPix =
      normalizePhoneDigits(
        pixPhone || bookingPayload?.phone || bookingPayload?.whatsapp || ""
      ) || undefined;
    if (!payerPhoneForPix || payerPhoneForPix.length < 10) {
      setPaymentError("Telefone do pagador inválido (informe DDD + número). ");
      return;
    }
    if (!payerPhoneForPix) {
      setPaymentError("Telefone do pagador é obrigatório para gerar o PIX.");
      return;
    }

    if (
      !billingZipCode ||
      !billingStreet ||
      !billingNumber ||
      !billingCity ||
      !billingState
    ) {
      setPaymentError("Endereço de cobrança é obrigatório para gerar o PIX.");
      return;
    }

    const selectedPixKey =
      safeForcedKey ||
      (bookingPayload?.whatsapp || "").replace(/\D/g, "") ||
      (bookingPayload?.phone || "").replace(/\D/g, "") ||
      bookingPayload?.email ||
      payerEmail ||
      null;

    setLoading(true);
    try {
      let appointmentId: string | null =
        bookingPayload?.id || bookingPayload?.appointmentId || null;

      if (!appointmentId) {
        if (!ensureHoldForPayment) {
          throw new Error("Não foi possível reservar o horário para pagamento.");
        }
        const hold = await ensureHoldForPayment();
        appointmentId = hold?.appointmentId || null;
      }

      if (!appointmentId) {
        throw new Error("Não foi possível reservar o horário para pagamento.");
      }

      const finalAmountCents = quotedAmountCents ?? amountCents;

      if (finalAmountCents == null) {
        throw new Error("Valor do pagamento não definido");
      }

      const payload = {
        transactionAmount: finalAmountCents / 100,
        description: bookingPayload?.serviceName || "Pagamento via PIX",
        payerEmail: emailForPix,
        payerCpf: payerCpfDigits,
        payerName: payerNameForPix || undefined,
        payerPhone: payerPhoneForPix,
        establishmentId,
        appointmentId,
        billingAddress: {
          zipCode: billingZipCode.replace(/\D/g, ""),
          street: billingStreet.trim(),
          number: billingNumber.trim(),
          complement: billingComplement || undefined,
          neighborhood: billingNeighborhood?.trim() || undefined,
          city: billingCity.trim(),
          state: billingState.trim(),
          country: "BR",
        },
      };

      // Importante: não usar URL hardcoded (localhost). Usa o client HTTP centralizado
      // que respeita a base configurada (e em produção usa a mesma origin + /api).
      const json = await payWithPix(payload);

      setPixResponse({
        ...json,
        providerPaymentId:
          json?.providerPaymentId || json?.id || json?.pix?.id || json?.pix?.charge_id,
      });
      setShowPixQrCode(true);
    } catch (err: any) {
      console.error("BookingPaymentModal.handlePixConfirm error:", err);
      setPaymentError(err?.message || "Erro ao iniciar pagamento PIX.");
    } finally {
      setLoading(false);
    }
  };

  const handlePixPaidCheck = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = !!opts?.silent;
      const providerPaymentId = pixResponse?.providerPaymentId || pixResponse?.id;
      const appointmentId =
        bookingPayload?.id ||
        bookingPayload?.appointmentId ||
        bookingPayload?.appointmentIdForPayment ||
        null;

      if (!establishmentId || !appointmentId || !providerPaymentId) {
        if (!silent) {
          setPaymentError(
            "Não foi possível verificar o pagamento (dados do PIX incompletos). Tente gerar um novo QR Code."
          );
        }
        return;
      }

      setPaymentError(null);
      setPixConfirming(true);
      try {
        const resp = await confirmPixPayment({
          establishmentId,
          appointmentId,
          providerPaymentId,
        });

        // Atualiza status exibido
        setPixResponse((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            status: resp?.status || prev?.status,
          };
        });

        if (resp?.status === "paid" || resp?.appointmentStatus === "confirmed") {
          onPaymentApproved?.(resp);
          onClose();
        }
      } catch (err: any) {
        if (!silent) {
          setPaymentError(err?.message || "Erro ao verificar pagamento PIX.");
        }
      } finally {
        setPixConfirming(false);
      }
    },
    [
      pixResponse?.providerPaymentId,
      pixResponse?.id,
      bookingPayload?.id,
      bookingPayload?.appointmentId,
      bookingPayload?.appointmentIdForPayment,
      establishmentId,
      onPaymentApproved,
      onClose,
    ]
  );

  // Auto-check do PIX: enquanto estiver no QR Code, verifica periodicamente.
  useEffect(() => {
    if (!show || paymentMethod !== "pix" || !showPixQrCode) return;
    if (!pixResponse?.id) return;
    if (pixAutoCheckStartedRef.current) return;

    pixAutoCheckStartedRef.current = true;

    let stopped = false;
    let attempts = 0;
    const maxAttempts = 30; // ~2m30s com 5s

    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      await handlePixPaidCheck({ silent: true });
      if (attempts >= maxAttempts) {
        stopped = true;
      }
    };

    const id = window.setInterval(() => {
      tick();
    }, 5000);

    // Faz uma checagem inicial rápida após 2s
    window.setTimeout(() => {
      tick();
    }, 2000);

    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [show, paymentMethod, showPixQrCode, pixResponse?.id, handlePixPaidCheck]);

  function extractPixKeyFromResponse(resp: any): string | null {
    try {
      if (!resp) return null;
      if (resp?.pix?.key) return resp.pix.key;
      const qr = resp?.pix?.qr_code || "";
      if (!qr) return null;
      const emailMatch = qr.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
      if (emailMatch) return emailMatch[0];
      const digits = qr.replace(/\D/g, "");
      if (digits.length >= 10 && digits.length <= 15) return digits;
      return null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    if (!show || !establishmentId) return;

    loadRefundFeePercentage(establishmentId);
  }, [show, establishmentId, loadRefundFeePercentage]);

  const refundFee = refundFeePercentage ?? 0;
  const clientRefundPercentage = Math.max(0, 100 - refundFee);

  const currentPixCode =
    pixResponse?.pix?.qr_code ||
    pixResponse?.pix?.key ||
    extractPixKeyFromResponse(pixResponse);

  const totalValue = displayAmount;

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 animate-fade-in">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.stopPropagation()}
      />

      <div className="relative w-[98vw] max-w-[1600px] h-[98vh] bg-white rounded-2xl shadow-2xl flex flex-col animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 text-white px-6 sm:px-8 py-5 shadow-lg flex-shrink-0">
          <button
            onClick={() => {
              onClose();
            }}
            className="absolute top-5 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200 backdrop-blur-sm"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="pr-12 flex items-center gap-3">
            <svg
              className="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
            <h2 className="text-xl sm:text-2xl font-bold text-white">Pagamento Online</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row">
          {/* Left Side - Payment Form (70%) */}
          <div className="flex-1 overflow-y-auto flex flex-col lg:flex-row">
            <div className="flex-1 px-6 sm:px-10 py-8 bg-white">
              <div className="max-w-2xl">
                <h3 className="text-lg font-semibold text-slate-900 mb-6">
                  Como você prefere pagar?
                </h3>

                <div className="space-y-3 pb-8">
                  {/* PIX */}
                  <div>
                    <button
                      onClick={() => handlePaymentMethodChange("pix")}
                      className={`w-full p-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${
                        paymentMethod === "pix"
                          ? "border-emerald-500 bg-emerald-50/50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                          <svg
                            className="w-7 h-7 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z"
                            />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-slate-900">PIX</div>
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    {paymentMethod === "pix" && (
                      <div className="mt-3 space-y-6 animate-fade-in">
                        {paymentError && (
                          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3 animate-fade-in">
                            <svg
                              className="w-6 h-6 text-red-600 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <div className="flex-1">
                              <h4 className="font-semibold text-red-900 text-sm mb-1">
                                Erro no pagamento
                              </h4>
                              <p className="text-red-800 text-sm">{paymentError}</p>
                            </div>
                          </div>
                        )}

                        {!showPixQrCode ? (
                          <div className="mt-3 space-y-5 animate-fade-in">
                            <div className="flex justify-center">
                              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-md border-2 border-emerald-200">
                                <svg
                                  className="w-12 h-12 text-emerald-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z"
                                  />
                                </svg>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <h3 className="text-2xl font-bold text-slate-900">
                                Pagamento via PIX
                              </h3>
                              <p className="text-slate-600">
                                Gere um QR Code e pague instantaneamente
                              </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto text-left">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Nome do pagador
                                </label>
                                <input
                                  type="text"
                                  value={cardName}
                                  onChange={(e) =>
                                    setCardName(e.target.value.toUpperCase())
                                  }
                                  placeholder="NOME COMPLETO"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200 uppercase"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  CPF do pagador
                                </label>
                                <input
                                  type="text"
                                  value={cardCpf}
                                  onChange={(e) => setCardCpf(formatCpf(e.target.value))}
                                  placeholder="000.000.000-00"
                                  maxLength={14}
                                  inputMode="numeric"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Telefone do pagador
                                </label>
                                <input
                                  type="text"
                                  value={pixPhone}
                                  onChange={(e) =>
                                    setPixPhone(formatPhone(e.target.value))
                                  }
                                  placeholder="(11) 99999-9999"
                                  maxLength={15}
                                  inputMode="numeric"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto text-left">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  CEP
                                </label>
                                <input
                                  type="text"
                                  value={billingZipCode}
                                  onChange={(e) =>
                                    setBillingZipCode(formatCep(e.target.value))
                                  }
                                  placeholder="00000-000"
                                  maxLength={9}
                                  inputMode="numeric"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  UF
                                </label>
                                <input
                                  type="text"
                                  value={billingState}
                                  onChange={(e) =>
                                    setBillingState(
                                      e.target.value.toUpperCase().slice(0, 2)
                                    )
                                  }
                                  placeholder="UF"
                                  maxLength={2}
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200 uppercase"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Rua
                                </label>
                                <input
                                  type="text"
                                  value={billingStreet}
                                  onChange={(e) => setBillingStreet(e.target.value)}
                                  placeholder="Rua/Avenida"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Número
                                </label>
                                <input
                                  type="text"
                                  value={billingNumber}
                                  onChange={(e) => setBillingNumber(e.target.value)}
                                  placeholder="Número"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Bairro
                                </label>
                                <input
                                  type="text"
                                  value={billingNeighborhood}
                                  onChange={(e) => setBillingNeighborhood(e.target.value)}
                                  placeholder="Bairro"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Cidade
                                </label>
                                <input
                                  type="text"
                                  value={billingCity}
                                  onChange={(e) => setBillingCity(e.target.value)}
                                  placeholder="Cidade"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                              <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                  Complemento (opcional)
                                </label>
                                <input
                                  type="text"
                                  value={billingComplement}
                                  onChange={(e) => setBillingComplement(e.target.value)}
                                  placeholder="Apto, Bloco, etc"
                                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all duration-200"
                                />
                              </div>
                            </div>

                            <button
                              onClick={() => {
                                handlePixConfirm();
                              }}
                              className="w-full max-w-md mx-auto bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
                              disabled={loading}
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z"
                                />
                              </svg>
                              {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Gerando...
                                </span>
                              ) : (
                                <>Gerar QR Code PIX</>
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 border border-emerald-100 space-y-6">
                            <div className="flex justify-center">
                              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md border-2 border-emerald-200">
                                <svg
                                  className="w-10 h-10 text-emerald-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                            </div>

                            <div className="text-center space-y-1">
                              <h3 className="text-xl font-bold text-slate-900">
                                Pagamento via PIX
                              </h3>
                              <p className="text-sm text-slate-600">
                                Gere um QR Code e pague instantaneamente
                              </p>
                            </div>

                            <div className="flex items-center justify-center gap-2 bg-white rounded-lg py-2 px-4 border border-emerald-200">
                              <svg
                                className="w-5 h-5 text-emerald-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span className="text-sm font-medium text-emerald-700">
                                QR Code gerado com sucesso!
                              </span>
                            </div>

                            <div className="flex justify-center">
                              <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-slate-200">
                                {pixResponse?.pix?.qr_code_base64 ? (
                                  // eslint-disable-next-line @next/next/no-img-element
                                  <img
                                    id="qrcode"
                                    src={`data:image/png;base64,${pixResponse.pix.qr_code_base64}`}
                                    alt="QR Code PIX"
                                    className="mx-auto"
                                    style={{
                                      width: 200,
                                      height: 200,
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : pixResponse?.pix?.qr_code ? (
                                  <pre className="text-xs bg-gray-100 p-3 rounded-lg break-words mb-4 text-left">
                                    {pixResponse.pix.qr_code}
                                  </pre>
                                ) : (
                                  // eslint-disable-next-line @next/next/no-img-element
                                  <img
                                    src={`/placeholder.svg?height=200&width=200&query=qr+code`}
                                    alt="QR Code PIX"
                                    className="w-48 h-48"
                                  />
                                )}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-slate-700 text-center">
                                Código PIX (copia e cola)
                              </label>
                              <textarea
                                readOnly
                                value={currentPixCode}
                                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-xs text-slate-600 font-mono resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                rows={3}
                              />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <button
                                onClick={() => {
                                  const keyToCopy = currentPixCode || "";
                                  if (keyToCopy) {
                                    navigator.clipboard?.writeText(keyToCopy);
                                    alert("Código PIX copiado!");
                                  }
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 002 2v14a2 2 0 002 2z"
                                  />
                                </svg>
                                Copiar código
                              </button>

                              <button
                                onClick={() => {
                                  const url = pixResponse?.pix?.ticket_url;
                                  if (url) {
                                    window.open(url, "_blank", "noopener,noreferrer");
                                  }
                                }}
                                className="bg-slate-500 hover:bg-slate-600 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                                  />
                                </svg>
                                Abrir ticket
                              </button>

                              <button
                                onClick={() => {
                                  setPixResponse(null);
                                  setPaymentError(null);
                                  setShowPixQrCode(false);
                                }}
                                className="bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3 px-4 rounded-lg shadow-md border border-slate-300 hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                                Novo QR Code
                              </button>
                            </div>
                            <button
                              onClick={() => handlePixPaidCheck()}
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-sm"
                              disabled={pixConfirming || loading}
                            >
                              {pixConfirming ? (
                                <span className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Verificando pagamento...
                                </span>
                              ) : (
                                <>
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  Já paguei
                                </>
                              )}
                            </button>

                            <div className="text-center">
                              <span className="text-sm text-slate-600">
                                Status:{" "}
                                <span className="font-medium text-amber-600">
                                  {pixResponse?.status || "Aguardando"}
                                </span>
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Cartão de crédito */}
                  <div>
                    <button
                      onClick={() => handlePaymentMethodChange("credit")}
                      className={`w-full p-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${
                        paymentMethod === "credit"
                          ? "border-purple-500 bg-purple-50/50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <svg
                            className="w-6 h-6 text-slate-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-slate-900">
                            Cartão de crédito
                          </div>
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    {paymentMethod === "credit" && (
                      <div className="mt-3 space-y-5 animate-fade-in">
                        {paymentError && (
                          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3 animate-fade-in">
                            <svg
                              className="w-6 h-6 text-red-600 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <div className="flex-1">
                              <h4 className="font-semibold text-red-900 text-sm mb-1">
                                Erro no pagamento
                              </h4>
                              <p className="text-red-800 text-sm">{paymentError}</p>
                            </div>
                          </div>
                        )}

                        <h3 className="text-base font-semibold text-slate-900">
                          Dados do cartão
                        </h3>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Número do cartão
                          </label>
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={(e) =>
                              setCardNumber(formatCardNumber(e.target.value))
                            }
                            placeholder="0000 0000 0000 0000"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Mês
                            </label>
                            <input
                              type="text"
                              value={cardMonth}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value.length <= 2) setCardMonth(value);
                              }}
                              placeholder="MM"
                              maxLength={2}
                              className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-center"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Ano
                            </label>
                            <input
                              type="text"
                              value={cardYear}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value.length <= 4) setCardYear(value);
                              }}
                              placeholder="AAAA"
                              maxLength={4}
                              className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-center"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              CVV
                            </label>
                            <input
                              type="text"
                              value={cardCvv}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value.length <= 4) setCardCvv(value);
                              }}
                              placeholder="•••"
                              maxLength={4}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-center"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Nome do titular
                          </label>
                          <input
                            type="text"
                            value={cardName}
                            onChange={(e) => setCardName(e.target.value.toUpperCase())}
                            placeholder="NOME COMO NO CARTÃO"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 uppercase"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            CPF do titular
                          </label>
                          <input
                            type="text"
                            value={cardCpf}
                            onChange={(e) => setCardCpf(formatCpf(e.target.value))}
                            placeholder="000.000.000-00"
                            maxLength={14}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                          />
                        </div>

                        <h3 className="text-base font-semibold text-slate-900">
                          Endereço de cobrança
                        </h3>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              CEP
                            </label>
                            <input
                              type="text"
                              value={billingZipCode}
                              onChange={(e) =>
                                setBillingZipCode(formatCep(e.target.value))
                              }
                              placeholder="00000-000"
                              maxLength={9}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              UF
                            </label>
                            <input
                              type="text"
                              value={billingState}
                              onChange={(e) =>
                                setBillingState(e.target.value.toUpperCase().slice(0, 2))
                              }
                              placeholder="UF"
                              maxLength={2}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 uppercase"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Rua
                          </label>
                          <input
                            type="text"
                            value={billingStreet}
                            onChange={(e) => setBillingStreet(e.target.value)}
                            placeholder="Rua/Avenida"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Número
                            </label>
                            <input
                              type="text"
                              value={billingNumber}
                              onChange={(e) => setBillingNumber(e.target.value)}
                              placeholder="Número"
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Complemento
                            </label>
                            <input
                              type="text"
                              value={billingComplement}
                              onChange={(e) => setBillingComplement(e.target.value)}
                              placeholder="Apto, sala..."
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Bairro
                            </label>
                            <input
                              type="text"
                              value={billingNeighborhood}
                              onChange={(e) => setBillingNeighborhood(e.target.value)}
                              placeholder="Bairro"
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Cidade
                            </label>
                            <input
                              type="text"
                              value={billingCity}
                              onChange={(e) => setBillingCity(e.target.value)}
                              placeholder="Cidade"
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cartão de débito */}
                  <div>
                    <button
                      onClick={() => handlePaymentMethodChange("debit")}
                      className={`w-full p-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-between ${
                        paymentMethod === "debit"
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <svg
                            className="w-6 h-6 text-slate-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="font-semibold text-slate-900">
                            Cartão de débito
                          </div>
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    {paymentMethod === "debit" && (
                      <div className="mt-3 space-y-5 animate-fade-in">
                        {paymentError && (
                          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3 animate-fade-in">
                            <svg
                              className="w-6 h-6 text-red-600 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <div className="flex-1">
                              <h4 className="font-semibold text-red-900 text-sm mb-1">
                                Erro no pagamento
                              </h4>
                              <p className="text-red-800 text-sm">{paymentError}</p>
                            </div>
                          </div>
                        )}

                        <h3 className="text-base font-semibold text-slate-900">
                          Dados do cartão
                        </h3>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Número do cartão
                          </label>
                          <input
                            type="text"
                            value={cardNumber}
                            onChange={(e) =>
                              setCardNumber(formatCardNumber(e.target.value))
                            }
                            placeholder="0000 0000 0000 0000"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Mês
                            </label>
                            <input
                              type="text"
                              value={cardMonth}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value.length <= 2) setCardMonth(value);
                              }}
                              placeholder="MM"
                              maxLength={2}
                              className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-center"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Ano
                            </label>
                            <input
                              type="text"
                              value={cardYear}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value.length <= 4) setCardYear(value);
                              }}
                              placeholder="AAAA"
                              maxLength={4}
                              className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-center"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              CVV
                            </label>
                            <input
                              type="text"
                              value={cardCvv}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, "");
                                if (value.length <= 4) setCardCvv(value);
                              }}
                              placeholder="•••"
                              maxLength={4}
                              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 text-center"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Nome do titular
                          </label>
                          <input
                            type="text"
                            value={cardName}
                            onChange={(e) => setCardName(e.target.value.toUpperCase())}
                            placeholder="NOME COMO NO CARTÃO"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200 uppercase"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            CPF do titular
                          </label>
                          <input
                            type="text"
                            value={cardCpf}
                            onChange={(e) => setCardCpf(formatCpf(e.target.value))}
                            placeholder="000.000.000-00"
                            maxLength={14}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all duration-200"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[420px] bg-slate-50 border-l border-b border-slate-200 rounded-bl-lg flex flex-col">
              <div className="flex-1 px-6 py-8">
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200">
                    <h3 className="text-xs font-semibold text-slate-500 mb-3 tracking-wider">
                      SERVIÇO
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {bookingPayload?.serviceName || "Serviço selecionado"}
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200">
                    <div className="flex items-end justify-between">
                      <span className="text-base font-medium text-slate-700">
                        Valor Total
                      </span>
                      <span className="text-4xl font-bold text-slate-900">
                        {totalValue}
                      </span>
                    </div>
                  </div>

                  {(paymentMethod === "credit" || paymentMethod === "debit") && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 animate-fade-in">
                      <div className="flex items-start gap-3">
                        <svg
                          className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <div>
                          <p className="text-xs font-semibold text-amber-900 mb-1.5">
                            Política de Cancelamento
                          </p>
                          <p className="text-xs text-amber-800 leading-relaxed">
                            Após o agendamento, em caso de cancelamento, o valor
                            reembolsado sofrerá uma taxa de {refundFee}%, sendo assim você
                            receberá {clientRefundPercentage}% do valor pago.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="border-t border-slate-200 px-6 py-4 bg-white flex-shrink-0">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      onClose();
                    }}
                    className="w-full px-6 py-3 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-all duration-200"
                  >
                    Cancelar
                  </button>

                  {(paymentMethod === "credit" || paymentMethod === "debit") && (
                    <button
                      className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      disabled={
                        loading ||
                        !paymentSdkReady ||
                        !cardNumber ||
                        !cardMonth ||
                        !cardYear ||
                        !cardCvv ||
                        !cardName ||
                        !cardCpf
                      }
                      onClick={() => {
                        handleCardPayment(paymentMethod === "debit");
                      }}
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Processando...
                        </span>
                      ) : !paymentSdkReady ? (
                        "⏳ Carregando..."
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                          {paymentMethod === "credit" && "Pagar com crédito"}
                          {paymentMethod === "debit" && "Pagar com débito"}
                        </>
                      )}
                    </button>
                  )}

                  {paymentMethod === "pix" && (
                    <button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
                      onClick={() => {
                        if (pixResponse) {
                          setShowPixQrCode(true);
                        } else {
                          handlePixConfirm();
                        }
                      }}
                      disabled={loading}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                        />
                      </svg>
                      {loading ? "Gerando..." : "Gerar QR Code"}
                    </button>
                  )}

                  {!paymentMethod && (
                    <div className="w-full bg-slate-200 text-slate-500 font-semibold py-3 px-6 rounded-lg text-center cursor-not-allowed">
                      Selecione uma forma de pagamento
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in {
            animation: fadeIn 0.2s ease-out;
          }

          .animate-slide-up {
            animation: slideUp 0.3s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
}

export default BookingPaymentModal;
